// /analytika/reporty — pondělní reporty analytiky do Slacku (22. 9. 2026).
// Report posílá každé pondělí ráno scheduled-pondelni-report.mjs. Tady je
// nastavení, archiv odeslaných reportů a ruční náhled nebo odeslání.
import React, { useState } from "react";
import { MessageSquare, Send, Eye, Settings2, History, ArrowUpRight } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, ErrorNote, SkeletonCards, Stitek, EmptyState, Modal } from "../../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, RadkyPrehledu, RadekPrehledu, HlaskaDlazdice } from "../../components/PrehledDlazdice.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { useApiData, apiFetch } from "../../lib/api.js";
import { toast } from "../../components/Toast.jsx";
import { datumText } from "./spolecne.jsx";

/** Slack mrkdwn do čitelného náhledu: *tučně*, citace a odkazy. */
function NahledZpravy({ text }) {
  const radky = String(text || "").split("\n");
  const inline = (t, k) => {
    const casti = [];
    const re = /\*([^*]+)\*|<([^|>]+)\|([^>]+)>/g;
    let posl = 0; let m; let i = 0;
    while ((m = re.exec(t))) {
      if (m.index > posl) casti.push(t.slice(posl, m.index));
      if (m[1]) casti.push(<b key={`${k}-${i++}`}>{m[1]}</b>);
      else casti.push(<a key={`${k}-${i++}`} href={m[2]} className="text-brand-deep underline">{m[3]}</a>);
      posl = re.lastIndex;
    }
    if (posl < t.length) casti.push(t.slice(posl));
    return casti;
  };
  return (
    <div className="space-y-1 text-[15px] leading-relaxed text-slate-800">
      {radky.map((r, i) => {
        if (!r.trim()) return <div key={i} className="h-2" />;
        if (r.startsWith("> ")) return <div key={i} className="border-l-4 border-slate-300 pl-3">{inline(r.slice(2), i)}</div>;
        return <div key={i}>{inline(r, i)}</div>;
      })}
    </div>
  );
}

export default function AnalytikaReporty() {
  const { data, loading, error, refetch, refreshing } = useApiData("/api/analytika-reporty", { bezKese: true });
  const [potvrdit, setPotvrdit] = useState(false);
  const [bezi, setBezi] = useState(false);
  const [otevreny, setOtevreny] = useState(null);

  async function spust(akce) {
    setBezi(true);
    try {
      const r = await apiFetch("/api/analytika-reporty", { method: "POST", body: JSON.stringify({ akce }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Nepodařilo se spustit");
      toast(akce === "odeslat" ? "Report se sestavuje a za chvíli přijde do Slacku" : "Náhled se sestavuje, za chvíli se objeví v seznamu", "success");
      setTimeout(refetch, 25000);
      setTimeout(refetch, 50000);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBezi(false);
    }
  }

  const reporty = data?.reporty || [];

  return (
    <Stranka>
      <GradientHeader
        icon={MessageSquare}
        title="Pondělní reporty"
        napoveda="Každé pondělí ráno přijde do Slacku shrnutí minulého týdne s doporučeními. Doporučení píše AI z čísel Analytiky. Každý report se tu uloží."
        loading={refreshing || bezi}
        akce={[
          { klic: "nahled", ikona: Eye, popis: "Sestavit náhled bez odeslání", onClick: () => spust("nahled"), disabled: bezi },
          { klic: "odeslat", ikona: Send, popis: "Poslat report do Slacku teď", onClick: () => setPotvrdit(true), disabled: bezi },
        ]}
      />

      {error && <ErrorNote onRetry={refetch}>Reporty se nepodařilo načíst: {error}</ErrorNote>}
      {loading && !data && <SkeletonCards count={2} />}

      {data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <KartaPrehledu>
            <HlavickaDlazdice ikona={Settings2} nazev="Nastavení reportu" />
            <RadkyPrehledu>
              <RadekPrehledu popisek="Kanál" hodnota={data.kanal} />
              <RadekPrehledu popisek="Kdy" hodnota="Pondělí 8:00 (v zimě 7:00)" />
              <RadekPrehledu popisek="Za jaké období" hodnota="Pondělí až neděle" />
              <RadekPrehledu popisek="Srovnání" hodnota="S předchozím týdnem" />
              <RadekPrehledu popisek="Doporučení" hodnota="Nejvýš 3, píše AI" />
            </RadkyPrehledu>
          </KartaPrehledu>

          <KartaPrehledu>
            <HlavickaDlazdice ikona={History} nazev="Reporty" odznak={String(reporty.length)} />
            {!reporty.length ? (
              <EmptyState icon={MessageSquare} title="Zatím žádný report" hint="První přijde v pondělí ráno. Náhled si můžeš sestavit hned tlačítkem s okem nahoře." />
            ) : (
              <div className="space-y-2 p-3 sm:p-4">
                {reporty.map((r) => (
                  <div key={r.id} className="polozka flex cursor-pointer items-center gap-3" onClick={() => setOtevreny(r)}>
                    <div className="min-w-0 flex-1">
                      <div className="text-base font-bold">Týden {datumText(r.tyden_od)} až {datumText(r.tyden_do)}</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {r.text
                          ? <Stitek ton={r.odeslano ? "ok" : "znacka"} velikost="mala">{r.odeslano ? "odesláno" : "náhled"}</Stitek>
                          : <Stitek ton="problem" velikost="mala">selhal</Stitek>}
                        <Stitek velikost="mala">{r.zdroj === "rucne" ? "ručně" : "automaticky"}</Stitek>
                        {r.chyba && <Stitek ton="problem" velikost="mala">{r.chyba.slice(0, 40)}</Stitek>}
                      </div>
                    </div>
                    <button type="button" className="btn-ikona" aria-label="Otevřít" title="Otevřít" onClick={(e) => { e.stopPropagation(); setOtevreny(r); }}>
                      <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <HlaskaDlazdice ton="zluta" nadpis="Náklady" text="Jeden report stojí zhruba 0,25 Kč za AI (asi 0,01 USD), přesná částka je v Náklady AI." />
          </KartaPrehledu>
        </div>
      )}

      <Modal
        open={!!otevreny}
        onClose={() => setOtevreny(null)}
        title={otevreny ? `Týden ${datumText(otevreny.tyden_od)} až ${datumText(otevreny.tyden_do)}` : ""}
        size="lg"
      >
        {otevreny && (otevreny.text
          ? <NahledZpravy text={otevreny.text} />
          : <ErrorNote>{otevreny.chyba || "Report se nepodařilo sestavit."} Náhled jde sestavit znovu tlačítkem s okem nahoře.</ErrorNote>)}
      </Modal>

      <ConfirmDialog
        open={potvrdit}
        title="Poslat report do Slacku?"
        description="Sestaví report za minulý týden a pošle ho hned do #automatizace."
        confirmLabel="Poslat"
        onConfirm={() => { setPotvrdit(false); spust("odeslat"); }}
        onCancel={() => setPotvrdit(false)}
        variant="primary"
      />
    </Stranka>
  );
}
